/**
 * Booking Service Hooks
 * React hooks for accessing booking management services
 */
import { useCCMS } from '../useCCMS';
/**
 * Hook to access all booking-related services
 * @returns BookingServices object containing all booking service instances
 */
export function useBookingsServices() {
    const { services } = useCCMS();
    if (!services?.bookings) {
        throw new Error('Booking services not available. Ensure CCMSProvider is configured with booking services.');
    }
    return services.bookings;
}
/**
 * Hook to access BookableResourceService
 */
export function useBookableResourceService() {
    return useBookingsServices().bookableResource;
}
/**
 * Hook to access BookableResourceTypeService
 */
export function useBookableResourceTypeService() {
    return useBookingsServices().bookableResourceType;
}
/**
 * Hook to access BookableResourceStatusService
 */
export function useBookableResourceStatusService() {
    return useBookingsServices().bookableResourceStatus;
}
/**
 * Hook to access BookableResourceImageService
 */
export function useBookableResourceImageService() {
    return useBookingsServices().bookableResourceImage;
}
/**
 * Hook to access BookableResourceBlockoutService
 */
export function useBookableResourceBlockoutService() {
    return useBookingsServices().bookableResourceBlockout;
}
/**
 * Hook to access BookableResourceBlockoutTypeService
 */
export function useBookableResourceBlockoutTypeService() {
    return useBookingsServices().bookableResourceBlockoutType;
}
/**
 * Hook to access BookingService (main booking operations)
 */
export function useBookingService() {
    return useBookingsServices().booking;
}
/**
 * Hook to access BookingStatusService
 */
export function useBookingStatusService() {
    return useBookingsServices().bookingStatus;
}
/**
 * Hook to access BookingLogService
 */
export function useBookingLogService() {
    return useBookingsServices().bookingLog;
}
/**
 * Hook to access BookingAccessoryService
 */
export function useBookingAccessoryService() {
    return useBookingsServices().bookingAccessory;
}
/**
 * Hook to access BookingSettingService
 */
export function useBookingSettingService() {
    return useBookingsServices().bookingSetting;
}
/**
 * Hook to access BookingLogMediaService
 */
export function useBookingLogMediaService() {
    return useBookingsServices().bookingLogMedia;
}
/**
 * Hook to access BookingStatusHistoryService
 */
export function useBookingStatusHistoryService() {
    return useBookingsServices().bookingStatusHistory;
}
/**
 * Hook to access BookableResourceNoteService
 */
export function useBookableResourceNoteService() {
    return useBookingsServices().bookableResourceNote;
}
/**
 * Hook to access BookingRecurrenceService (Phase 7a — recurring booking templates)
 */
export function useBookingRecurrenceService() {
    return useBookingsServices().bookingRecurrence;
}
/**
 * Hook to access BookingWaitlistService (Phase 7b — resource waitlist)
 */
export function useBookingWaitlistService() {
    return useBookingsServices().bookingWaitlist;
}
/**
 * Hook to access BookingInventoryAuditService (RFID — scan everything in a location)
 */
export function useBookingInventoryAuditService() {
    return useBookingsServices().bookingInventoryAudit;
}
/**
 * Hook to access BookingPlatformSettingsService (singleton feature + channel kill switches)
 */
export function useBookingPlatformSettingsService() {
    return useBookingsServices().bookingPlatformSettings;
}
//# sourceMappingURL=useBookingsServices.js.map